import React from 'react';
import dayjs from 'dayjs';
import {
  Box, Paper, TextField, MenuItem, Button, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, Tooltip, Stack, Alert
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';

import Client from '../Client';
import Config from '../ClientConfig';

class CurrentStockSnapshot extends React.Component {
  constructor(props) {
    super(props);

    const today = dayjs().format('YYYY-MM-DD');

    this.state = {
      client: new Client('none'),
      loading: false,

      // lookups
      locations: [],
      products: [],
      productNameMap: {},

      // filters
      filters: { date: today, location: '', product: '' },

      // data
      all_rows: [],
      rows: [],

      // ui
      page: 0,
      rowsPerPage: -1,

      // info/warnings
      infoText: '',
      errorText: '',
    };
  }

  componentDidMount() {
    this.fetchLookups();
  }

  // ---------- utils ----------

  // Map numeric unitType -> label on the frontend
  unitLabel = (unitType) => {
    const map = { 0: 'pc', 1: 'kg', 3: 'other', 9: 'undefined' };
    const k = Number(unitType);
    return Object.prototype.hasOwnProperty.call(map, k) ? map[k] : 'undefined';
  };

  escapeCSV(value) {
    const s = (value == null ? '' : String(value));
    if (/[",\n]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
    return s;
  }

  getProductName = (prodId) => {
    if (!prodId) return '';
    const map = this.state.productNameMap || {};
    return map[String(prodId)] || String(prodId);
  };

  // Display: show decimals only if they exist, max 3 decimals, no trailing zeros
  formatQtyDisplay = (val) => {
    const n = Number(val);
    if (!Number.isFinite(n)) return '';
    if (Math.abs(n - Math.trunc(n)) < 1e-9) {
    return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
    }
    return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 3 });
  };

  // CSV: dot as decimal separator, no thousands separator, max 3 decimals, no trailing zeros
  formatQtyCsv = (val) => {
    const n = Number(val);
    if (!Number.isFinite(n)) return '';
    if (Math.abs(n - Math.trunc(n)) < 1e-9) return String(Math.trunc(n));
    return n.toFixed(3).replace(/\.?0+$/, '');
  };

  applyProductFilter(rows, productId) {
    if (!productId) return rows || [];
    return (rows || []).filter(r => String(r.productId) === String(productId));
  }

  // ---------- lookups ----------
  fetchLookups() {
    this.setState({ loading: true, errorText: '', infoText: '' });
    try {
      const locationsResp = this.state.client.getData(
        (Config.API_URL_LOCATIONS || '') + 'list_locations/',
        { status: 1 }
      ) || {};

      const productsResp  = this.state.client.getData(
        (Config.API_URL_PRODUCT || '') + 'list_products/',
        { status: 2, isPurchased: 1, orderBy_name: true }
      ) || {};

      const locs = Array.isArray(locationsResp.locations) ? locationsResp.locations : [];
      const locations = locs.sort((a, b) => {
        const an = ((a && (a.name || a.description)) || '').toLowerCase();
        const bn = ((b && (b.name || b.description)) || '').toLowerCase();
        return an.localeCompare(bn);
      });

      const products  = Array.isArray(productsResp.products) ? productsResp.products : [];

      const productNameMap = {};
      for (let i = 0; i < products.length; i++) {
        const p = products[i] || {};
        const pid = p.iref || p.uid || p.code || p.id || p.product;
        const pname = p.name || p.productName || p.description || p.code || pid;
        if (pid) productNameMap[String(pid)] = String(pname);
      }

      this.setState({ locations, products, productNameMap, loading: false }, () => {
        if (this.state.filters.location) this.fetchData();
      });
    } catch (e) {
      this.setState({ loading: false, errorText: 'Failed to load lookups.' });
    }
  }

  // ---------- query ----------
  buildQuery() {
    const f = this.state.filters || {};
    const date = f.date || dayjs().format('YYYY-MM-DD');
    const q = { asof: date };
    if (f.location) q.location = f.location;
    return q;
  }

  // ---------- fetch ----------
  fetchData = () => {
    const st = this.state;
    const filters = st.filters || {};
    if (!filters.location) {
      this.setState({ rows: [], all_rows: [], page: 0, infoText: '', errorText: '' });
      return;
    }

    this.setState({ loading: true, page: 0, infoText: '', errorText: '' });

    const q = this.buildQuery();

    const baseUrl = (Config.API_URL_REPORTS_TRANSACTIONS || Config.API_URL_REPORTS || '');
    const url = baseUrl + 'current_stock/';
    let resp = {};
    try {
      resp = st.client.getData(url, q) || {};
    } catch (e) {
      this.setState({ loading: false, errorText: 'Request failed.' });
      return;
    }

    if (resp && resp.error) {
      this.setState({ loading: false, errorText: resp.detail || 'Request failed.' });
      return;
    }

    const list = Array.isArray(resp.items) ? resp.items
      : (Array.isArray(resp.rows) ? resp.rows
      : (Array.isArray(resp.snapshot) ? resp.snapshot : []));

    if (!list || !list.length) {
      this.setState({ rows: [], all_rows: [], loading: false, infoText: 'No stock lines found.' });
      return;
    }

    let rows = (list || []).map((ln, idx) => {
      const pid = String((ln && (ln.product || ln.productId || ln.id)) || idx);
      const qty = Number(
        (ln && ln.qty != null) ? ln.qty :
        ((ln && ln.quantity != null) ? ln.quantity :
        ((ln && ln.stockQty != null) ? ln.stockQty : 0))
      );
      const unitStr = (ln && ln.unit != null) ? this.unitLabel(ln.unit)
                    : ((ln && ln.unitType != null) ? this.unitLabel(ln.unitType) : '');

      // try to get isPurchased from products lookup
      const products = this.state.products || [];
      let isPurchased = 0;
      for (let i = 0; i < products.length; i++) {
        const p = products[i] || {};
        const pidIref = p.iref || p.uid || p.code || String(p.id || '');
        if (String(pidIref) === pid) {
          isPurchased = (p.isPurchased != null) ? Number(p.isPurchased) : 0;
          break;
        }
      }
      if (ln && ln.isPurchased != null) {
        isPurchased = Number(ln.isPurchased);
      }

      return {
        id: (ln && ln.id != null) ? ln.id : pid,
        productId: pid,
        productName: (ln && ln.productName) ? ln.productName : (this.state.productNameMap[pid] || pid),
        unit: unitStr,
        qty: qty,
        isPurchased: isPurchased
      };
    });

    // filter only isPurchased = 1 and qty != 0
    rows = rows.filter(r => Number(r.isPurchased) === 1 && Math.abs(r.qty) > 1e-9);

    const filtered = this.applyProductFilter(rows, filters.product);

    const asof = dayjs(filters.date).format('YYYY-MM-DD');
    const eodPrev = dayjs(filters.date).subtract(1, 'day').format('YYYY-MM-DD');

    this.setState({
      all_rows: rows,
      rows: filtered,
      loading: false,
      infoText: rows.length ? ('Stock as of ' + asof + ' (end of ' + eodPrev + ').') : 'No stock lines after filtering.'
    });
  };

  // ---------- csv ----------
  exportCSV = () => {
    const st = this.state;
    const filters = st.filters || {};
    const locations = st.locations || [];
    const productNameMap = st.productNameMap || {};
    const rows = st.rows || [];

    const asof = dayjs(filters.date || dayjs().format('YYYY-MM-DD')).format('YYYY-MM-DD');
    const eodPrev = dayjs(filters.date || dayjs().format('YYYY-MM-DD')).subtract(1, 'day').format('YYYY-MM-DD');

    const locId = filters.location;
    const locObj = (locations || []).find(l => String(l.iref || l.uid || l.id) === String(locId));
    const locationLabel = locId ? (((locObj && (locObj.name || locObj.description)) || String(locId))) : 'All';

    const productLabel = filters.product
      ? (productNameMap[String(filters.product)] || String(filters.product))
      : 'All';

    const generatedAt = dayjs().format('YYYY-MM-DD HH:mm:ss');

    const summaryRows = [
      ['Summary'],
      ['Date', asof + ' (end of ' + eodPrev + ')'],
      ['Location', locationLabel],
      ['Product filter', productLabel],
      ['Lines', String(rows.length)],
      ['Generated at', generatedAt],
      ['']
    ].map(row => row.map(v => this.escapeCSV(v)).join(','));

    const header = ['Product','Unit','Qty'].join(',');

    const lines = rows.map(r => [
      this.escapeCSV(r.productName || r.productId),
      this.escapeCSV(r.unit || ''),
      this.formatQtyCsv(r.qty),
    ].join(','));

    const csv = [...summaryRows, header, ...lines].join('\r\n');

    const dateSafe = (asof || 'date').replace(/[^0-9-]/g, '');
    const locSafe = (locationLabel || 'location').replace(/[^a-z0-9-_]+/gi, '_');
    const fname = 'CurrentStock_' + dateSafe + '_' + locSafe + '.csv';

    const blob = new Blob(["\uFEFF" + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = fname;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // ---------- render ----------
  renderFilters() {
    const st = this.state;
    const filters = st.filters || {};
    const locations = st.locations || [];
    const products = st.products || [];

    const productOptions = [
      { label: 'All', value: '' },
      ...products.map(p => {
        const value = String(p.iref || p.uid || p.code || p.id || p.product || '');
        const label = p.name || p.productName || p.description || p.code || value;
        return { value, label };
      }),
    ];

    const selectedVal = String(((filters && filters.product) != null ? filters.product : ''));
    const productValue = productOptions.find(o => o.value === selectedVal) || productOptions[0];

    return (
      <Paper
        elevation={1}
        sx={{
          flexShrink: 0,
          p: { xs: 0.5, sm: 0.75 },
          mb: 0.75,
          borderRadius: '14px',
          overflow: 'hidden',
        }}
      >
        <Stack
          direction="row"
          sx={{
            gap: 0.75,
            flexWrap: 'wrap',
            alignItems: 'center',
            '& .MuiButton-root': {
              height: 32,
              borderRadius: '11px',
              textTransform: 'none',
              fontWeight: 700,
            },
          }}
        >
          <TextField
            label="Date"
            type="date"
            value={filters.date}
            onChange={(e) => this.setState(prev => ({ filters: { ...(prev.filters || {}), date: e.target.value } }))}
            size="small"
            margin="dense"
            sx={{
              width: { xs: 'calc(50% - 6px)', sm: 150 },
              minWidth: 0,
              '& .MuiInputBase-root': {
                height: 34,
                borderRadius: '12px',
              },
              '& .MuiInputBase-input': {
                fontSize: {
                  xs: '16px',
                  sm: '0.84rem',
                },
              },
            }}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            select
            label="Location"
            value={filters.location}
            onChange={(e) => this.setState(prev => ({ filters: { ...(prev.filters || {}), location: e.target.value } }))}
            size="small"
            margin="dense"
            sx={{
              width: { xs: 'calc(50% - 6px)', sm: 240 },
              minWidth: 0,
              '& .MuiInputBase-root': {
                height: 34,
                borderRadius: '12px',
              },
              '& .MuiInputBase-input': {
                fontSize: {
                  xs: '16px',
                  sm: '0.84rem',
                },
              },
            }}
          >
            <MenuItem value="">All</MenuItem>
            {locations.map((loc) => (
              <MenuItem key={String(loc.iref || loc.uid || loc.id)} value={String(loc.iref || loc.uid || loc.id)}>
                {loc.name || loc.description || String(loc.iref || loc.uid || loc.id)}
              </MenuItem>
            ))}
          </TextField>

          <Box sx={{ flexGrow: 1, minWidth: 8 }} />

          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
            <Button variant="contained" size="small" onClick={this.fetchData} disabled={this.state.loading}>
              Apply
            </Button>
            <Button variant="outlined" size="small" onClick={this.exportCSV} disabled={!this.state.rows.length}>
              CSV
            </Button>
          </Stack>

          <Autocomplete
            size="small"
            value={productValue}
            options={productOptions}
            isOptionEqualToValue={(o, v) => ((o && o.value) || '') === ((v && v.value) || '')}
            onChange={(_e, opt) => {
              const newVal = opt ? opt.value : '';
              this.setState(prev => {
                const nextFilters = { ...(prev.filters || {}), product: newVal };
                const base = prev.all_rows || [];
                const rows = this.applyProductFilter(base, newVal);
                return { filters: nextFilters, rows, page: 0 };
              });
            }}
            sx={{
              width: {
                xs: '100%',
                sm: 320,
              },
              minWidth: 0,
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Product"
                margin="dense"
                sx={{
                  minWidth: {
                    xs: '100%',
                    sm: 220,
                  },
                  maxWidth: {
                    xs: '100%',
                    sm: 320,
                  },
                  '& .MuiInputBase-root': {
                    height: 34,
                    borderRadius: '12px',
                  },
                  '& .MuiInputBase-input': {
                    fontSize: {
                      xs: '16px',
                      sm: '0.84rem',
                    },
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    textAlign: 'left',
                  },
                }}
                InputProps={{ ...(params.InputProps || {}) }}
                inputProps={{ ...(params.inputProps || {}), title: (params.inputProps ? params.inputProps.value : undefined) }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} title={option.label}>
                <div
                  style={{
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'normal',
                    textAlign: 'left',
                    maxWidth: 300,
                  }}
                >
                  {option.label}
                </div>
              </li>
            )}
          />
        </Stack>

        {!!this.state.infoText && (
          <Alert severity="info" sx={{ mt: 1 }}>{this.state.infoText}</Alert>
        )}
        {!!this.state.errorText && (
          <Alert severity="error" sx={{ mt: 1 }}>{this.state.errorText}</Alert>
        )}
      </Paper>
    );
  }

  renderTable() {
    const { loading, rows, page, rowsPerPage } = this.state;
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    const paged = rowsPerPage > 0 ? rows.slice(start, start + rowsPerPage) : rows;

    return (
      <Paper
        elevation={1}
        sx={{
          position: 'relative',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '14px',
        }}
      >
        <TableContainer sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <Table
            stickyHeader
            size="small"
            sx={{
              tableLayout: 'fixed',
              width: '100%',
              '& .MuiTableCell-stickyHeader': {
                backgroundColor: '#f8fafc',
                zIndex: 3,
              },
              '& .MuiTableCell-root': {
                py: { xs: 0.45, sm: 0.65 },
                px: { xs: 0.5, sm: 0.9 },
                fontSize: { xs: '0.74rem', sm: '0.86rem' },
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, width: '68%' }}>Product</TableCell>
                <TableCell sx={{ fontWeight: 700, width: '12%' }}>Unit</TableCell>
                <TableCell sx={{ fontWeight: 700, width: '20%' }} align="right">Qty</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paged.map((row, idx) => (
                <TableRow key={row.id || (String(page) + '-' + String(idx))} hover>
                  <TableCell
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Tooltip title={row.productId} placement="top" arrow>
                      <span>{row.productName || row.productId}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{row.unit || ''}</TableCell>
                  <TableCell align="right">{this.formatQtyDisplay(row.qty)}</TableCell>
                </TableRow>
              ))}

              {!paged.length && !loading && (
                <TableRow>
                  <TableCell colSpan={3} align="center">No data</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={rows.length}
          page={page}
          onPageChange={(_e, newPage) => this.setState({ page: newPage })}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => this.setState({ rowsPerPage: Number(e.target.value), page: 0 })}
          rowsPerPageOptions={[10, 25, { value: -1, label: 'All' }]}
          sx={{
            flexShrink: 0,
            borderTop: '1px solid rgba(15, 23, 42, 0.06)',
            minHeight: { xs: 38, sm: 50 },
            overflow: 'hidden',
            '& .MuiTablePagination-toolbar': {
              minHeight: { xs: 38, sm: 50 },
              px: { xs: 0.25, sm: 1 },
              flexWrap: 'nowrap',
            },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontSize: { xs: '0.72rem', sm: '0.86rem' },
              m: 0,
            },
            '& .MuiIconButton-root': {
              p: { xs: 0.35, sm: 0.5 },
            },
          }}
        />

        {loading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'rgba(255,255,255,0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Paper>
    );
  }

  render() {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          maxHeight: '100%',
          minHeight: 0,
          minWidth: 0,
          overflow: 'hidden',
          bgcolor: 'background.default',
          p: {
            xs: 0.5,
            sm: 0.85,
            md: 1.1,
          },
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {this.renderFilters()}
        {this.renderTable()}
      </Box>
    );
  }
}

export default CurrentStockSnapshot;